import { cn } from '@/lib/utils';
import { WarningOctagon as WarningOctagonIcon } from '@phosphor-icons/react';
import type { AllocationRow, HorizonMeta, Product } from '../data';
import { HORIZON, findCustomer, findProduct, whColor } from '../data';

function boundaryAt(horizon: HorizonMeta[], i: number) {
  if (i === 0) return { year: false, quarter: false, month: false };
  const wk = horizon[i];
  const prev = horizon[i - 1];
  const year = wk.year !== prev.year;
  const wNum = parseInt(wk.label.slice(1), 10);
  const quarter = year || wNum === 14 || wNum === 27 || wNum === 40;
  const month = quarter || i % 4 === 0;
  return { year, quarter, month };
}

function quarterLabel(wk: HorizonMeta) {
  const wNum = parseInt(wk.label.slice(1), 10);
  const q = wNum <= 13 ? 1 : wNum <= 26 ? 2 : wNum <= 39 ? 3 : 4;
  return { q, label: `Q${q} ${wk.year}`, year: wk.year };
}

function buildQuarterSpans(horizon: HorizonMeta[]) {
  const spans: {
    q: number;
    year: number;
    label: string;
    startIdx: number;
    span: number;
  }[] = [];
  let curr: (typeof spans)[0] | null = null;
  horizon.forEach((wk, i) => {
    const { q, label, year } = quarterLabel(wk);
    if (!curr || curr.q !== q || curr.year !== year) {
      if (curr) spans.push(curr);
      curr = { q, year, label, startIdx: i, span: 1 };
    } else {
      curr.span += 1;
    }
  });
  if (curr) spans.push(curr);
  return spans;
}

const thBase =
  'text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border sticky top-0 z-[2] p-[7px_10px]';

function HorizonHeader({ horizon }: { horizon: HorizonMeta[] }) {
  const quarters = buildQuarterSpans(horizon);
  return (
    <thead>
      <tr>
        <th
          rowSpan={2}
          className="sticky left-0 top-0 z-[4] min-w-[220px] border-b border-border bg-stone-50 p-[7px_10px] text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
        >
          Customer · Product
        </th>
        <th
          rowSpan={2}
          className="sticky left-[220px] top-0 z-[4] min-w-[80px] border-b border-r-2 border-r-stone-700 border-b-border bg-stone-50 p-[7px_10px] text-right text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
        >
          Base / wk
        </th>
        {quarters.map((qs, qi) => {
          const isPrevYear = qi > 0 && quarters[qi - 1].year !== qs.year;
          return (
            <th
              key={qi}
              colSpan={qs.span}
              className={cn(
                'top-0 whitespace-nowrap border-b border-border p-[6px_8px] text-left text-[10px] font-semibold tracking-wide text-stone-700',
                qs.q % 2 === 0 ? 'bg-stone-50' : 'bg-white',
              )}
              style={{
                position: 'sticky',
                borderLeft: isPrevYear
                  ? '2px solid var(--stone-700, #44403c)'
                  : qi > 0
                    ? '1.5px solid var(--stone-400, #a8a29e)'
                    : '1px solid transparent',
              }}
            >
              <span className="inline-flex items-baseline gap-1.5">
                <span className="text-[13px] font-semibold tracking-tight text-stone-900">
                  Q{qs.q}
                </span>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {qs.year}
                </span>
              </span>
            </th>
          );
        })}
      </tr>
      <tr>
        {horizon.map((wk, i) => {
          const b = boundaryAt(horizon, i);
          return (
            <th
              key={i}
              className={cn(
                'min-w-[38px] w-[38px] p-[4px_2px] text-center text-[10px] font-mono border-b border-border',
                wk.isCurrent
                  ? 'bg-indigo-50 text-indigo-700 font-bold'
                  : wk.isPast
                    ? 'bg-stone-50 text-muted-foreground'
                    : 'bg-white text-stone-700',
              )}
              style={{
                position: 'sticky',
                top: 28,
                zIndex: 2,
                fontWeight: wk.isCurrent ? 700 : 500,
                borderLeft: b.year
                  ? '2px solid var(--stone-700, #44403c)'
                  : b.quarter
                    ? '1.5px solid var(--stone-400, #a8a29e)'
                    : b.month
                      ? '1px solid var(--stone-200, #e7e5e4)'
                      : '1px solid transparent',
              }}
            >
              <div>{wk.label}</div>
              {wk.isCurrent && (
                <div className="text-[8px] font-bold tracking-wider text-indigo-700">
                  NOW
                </div>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

function HorizonCell({
  wk,
  boundary,
}: {
  wk: {
    alloc: number;
    consumed: number | null;
    over: boolean;
    isCurrent: boolean;
    isFuture: boolean;
    isPast: boolean;
  };
  boundary: { year: boolean; quarter: boolean; month: boolean };
}) {
  const isOver = wk.over;
  const isCurrent = wk.isCurrent;
  const isFuture = wk.isFuture;
  let consumePct = 0;
  if (wk.consumed != null && wk.alloc > 0)
    consumePct = Math.min(100, (wk.consumed / wk.alloc) * 100);

  let bg = '#fff';
  let textColor = 'var(--stone-900, #1c1917)';
  let border = isCurrent ? '1px solid #4338ca' : '1px solid transparent';

  if (isOver) {
    bg = '#fef2f2';
    textColor = '#991b1b';
    border = '1px solid #fca5a5';
  } else if (isCurrent) {
    bg = '#eef2ff';
  } else if (isFuture) {
    bg = '#fff';
    textColor = 'var(--stone-700, #44403c)';
  } else {
    bg = 'var(--stone-50, #fafaf9)';
    textColor = 'var(--stone-700, #44403c)';
  }

  return (
    <td
      className="relative min-w-[38px] w-[38px] h-9 border-b border-border p-0 align-middle"
      style={{
        background: bg,
        borderLeft: boundary.year
          ? '2px solid var(--stone-700, #44403c)'
          : boundary.quarter
            ? '1.5px solid var(--stone-400, #a8a29e)'
            : boundary.month
              ? '1px solid var(--stone-200, #e7e5e4)'
              : '1px solid var(--stone-100, #f5f5f4)',
      }}
    >
      <div
        className="absolute inset-px flex flex-col items-center justify-center rounded-sm"
        style={{
          border,
          background:
            isFuture && isOver
              ? 'repeating-linear-gradient(45deg, #fef2f2, #fef2f2 3px, #fee2e2 3px, #fee2e2 6px)'
              : 'transparent',
        }}
      >
        <div
          className="text-[11px] tabular-nums leading-none"
          style={{ fontWeight: isCurrent ? 700 : 500, color: textColor }}
        >
          {wk.alloc}
        </div>
        {wk.consumed != null && (
          <div className="relative mt-0.5 h-0.5 w-[70%] rounded-full bg-black/5">
            <div
              className="absolute left-0 top-0 bottom-0 rounded-full"
              style={{
                width: `${consumePct}%`,
                background: isOver ? '#dc2626' : '#525252',
              }}
            />
          </div>
        )}
      </div>
      {isOver && (
        <span className="absolute right-0.5 top-0.5 h-1 w-1 rounded-full bg-red-600" />
      )}
    </td>
  );
}

export function HorizonPivot({
  rows,
  dense,
  flagged,
  onFlag,
}: {
  rows: AllocationRow[];
  dense: boolean;
  flagged: Set<string>;
  onFlag: (rid: string) => void;
}) {
  const capped = rows.slice(0, 100);
  const truncated = rows.length - capped.length;
  const overWeeksByRow = capped.map(
    (r) => r.weekly.filter((w) => w.over).length,
  );

  return (
    <div className="overflow-hidden rounded-b-xl border border-t-0 border-border bg-white">
      {/* Legend */}
      <div className="flex items-center gap-3.5 border-b border-border bg-stone-50 px-3 py-2 text-[11px] text-muted-foreground">
        <span className="font-semibold text-stone-800">52-week horizon</span>
        <span className="inline-flex items-center gap-1">
          <span className="h-3 w-3 rounded-sm border border-stone-300 bg-stone-50" />{' '}
          Past · actual
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-3 w-3 rounded-sm border border-indigo-700 bg-indigo-50" />{' '}
          Current week
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-3 w-3 rounded-sm border border-stone-200 bg-white" />{' '}
          Future · planned
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-3 w-3 rounded-sm border border-red-300 bg-red-50" />{' '}
          Over-consumed
        </span>
        <span className="inline-flex items-center gap-1">
          <span
            className="h-3 w-3 rounded-sm border border-red-300"
            style={{
              background:
                'repeating-linear-gradient(45deg, #fef2f2, #fef2f2 2px, #fee2e2 2px, #fee2e2 4px)',
            }}
          />
          Forecast at-risk
        </span>
        <div className="flex-1" />
        <span>Tip: scroll horizontally to see all 52 weeks</span>
      </div>

      <div
        className="overflow-auto"
        style={{ maxHeight: 'calc(100vh - 400px)' }}
      >
        <table className="border-collapse text-xs">
          <HorizonHeader horizon={HORIZON} />
          <tbody>
            {capped.map((row, ri) => {
              const cust = findCustomer(row.customer);
              const prod = findProduct(row.product);
              const overCount = overWeeksByRow[ri];
              const isFlagged = flagged.has(row.rid);
              return (
                <tr key={row.rid}>
                  <td
                    className="sticky left-0 z-[1] min-w-[220px] border-b border-border bg-white p-[6px_10px]"
                    style={{
                      borderLeft: isFlagged
                        ? '3px solid #b45309'
                        : '3px solid transparent',
                    }}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {cust.id}
                      </span>
                      <span className="max-w-[130px] truncate text-xs font-medium text-stone-900">
                        {cust.name}
                      </span>
                    </div>
                    <div className="max-w-[200px] truncate text-[10px] text-muted-foreground">
                      <span className="font-mono">{prod.id}</span> · {prod.name}
                    </div>
                  </td>
                  <td className="sticky left-[220px] z-[1] min-w-[80px] border-b border-r-2 border-r-border border-b-border bg-white p-[6px_10px] text-right">
                    <div className="text-[13px] font-semibold tabular-nums text-stone-900">
                      {row.qty['S-MAY13']}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {prod.uom}
                    </div>
                    {overCount > 0 && (
                      <div className="mt-0.5 inline-flex items-center gap-0.5 rounded bg-red-100 px-1.5 py-px text-[9px] font-bold text-red-800">
                        <WarningOctagonIcon size={9} />
                        {overCount}
                      </div>
                    )}
                  </td>
                  {row.weekly.map((wk, i) => (
                    <HorizonCell
                      key={i}
                      wk={wk}
                      boundary={boundaryAt(HORIZON, i)}
                    />
                  ))}
                </tr>
              );
            })}
            {capped.length === 0 && (
              <tr>
                <td
                  colSpan={HORIZON.length + 2}
                  className="p-10 text-center text-[13px] text-muted-foreground"
                >
                  No allocations match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {truncated > 0 && (
        <div className="border-t border-border bg-stone-50 px-3 py-2 text-[11px] text-muted-foreground">
          Showing first 100 rows · {truncated} more — narrow your filters to see
          them in this view.
        </div>
      )}
    </div>
  );
}
